/**
 * Structured answer card family for AskBot.
 *
 * Dispatches to the right card based on resultType:
 *   aggregate   → MetricCard   (one or a few big numbers)
 *   ranked_list → LeaderboardCard (rank badge + inline bar + value)
 *   list        → RecordCard   (actionable records with Call / Open)
 *
 * TrendCard is used for ranked_list / aggregate when a date column is present.
 * All cards use only the project's existing Card + design tokens — no new library.
 */

import { useNavigate } from "react-router-dom";
import { Phone, ExternalLink, Trophy, TrendingUp, BarChart3, ChevronDown, ChevronUp, MessageCircle, Users } from "lucide-react";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { InlineBar } from "./InlineBar";
import { Sparkline } from "./Sparkline";
import { buildRecordLink } from "@/lib/askbotDeeplinks";
import type { ResultType } from "@/services/askbotService";

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function fmtMoney(v: number): string {
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(1)}Cr`;
  if (v >= 100_000)    return `₹${(v / 100_000).toFixed(1)}L`;
  return `₹${v.toLocaleString("en-IN")}`;
}

function isMoneyCol(name: string): boolean {
  const n = name.toLowerCase();
  return (
    n.includes("amount") || n.includes("disburs") || n.includes("payout") ||
    n.includes("sanction") || n.includes("value") ||
    (n.includes("total") && !n.includes("count") && !n.includes("leads"))
  );
}

function isRateCol(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes("rate") || n.includes("pct") || n.includes("percent") || n.includes("ratio");
}

function isDateCol(name: string): boolean {
  const n = name.toLowerCase();
  // "monthly_target", "monthly_avg" etc. are numeric targets, not date columns
  if (n.startsWith("monthly_") || n.endsWith("_monthly")) return false;
  return n.includes("month") || n.includes("week") || n.includes("date") || n.includes("period") || n.includes("year");
}

function isNumericCol(name: string, v: unknown): boolean {
  return typeof v === "number";
}

function fmtValue(colName: string, v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") {
    if (isMoneyCol(colName)) return fmtMoney(v);
    if (isRateCol(colName))  return `${v.toFixed(1)}%`;
    return v.toLocaleString("en-IN");
  }
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
    if (/^\d{4}-\d{2}$/.test(v)) {
      // "2025-06" → "Jun 2025"
      try {
        const d = new Date(`${v}-01`);
        return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
      } catch { return v; }
    }
  }
  return String(v);
}

function colLabel(col: string): string {
  const LABELS: Record<string, string> = {
    total_disbursement: "Disbursement",
    disbursement_total: "Disbursement",
    total_disbursed:    "Disbursed",
    file_count:         "Files",
    lead_count:         "Leads",
    total_leads:        "Leads",
    conversion_rate:    "Conversion",
    avg_disbursement:   "Avg Disburse",
    count:              "Count",
  };
  return LABELS[col.toLowerCase()] ?? col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function whatsappHref(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${normalized}`;
}

// Find a specific column by pattern keywords
function findCol(keys: string[], ...patterns: string[]): string | undefined {
  for (const pat of patterns) {
    const found = keys.find((k) => k.toLowerCase().includes(pat));
    if (found) return found;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Internal-column stripping
// ---------------------------------------------------------------------------

/**
 * `_raw` is the SQL mirror's copy of the entire Firestore document. It exists
 * for the SQL layer to json_extract from, never for display: a generated query
 * that selects it bare (or a `SELECT *`) would otherwise dump the whole
 * document into the card as an unreadable JSON blob. Dropped by name, and by
 * value shape for anything else that reaches us serialised as a JSON object.
 */
function isInternalCol(col: string): boolean {
  return /(^|_)raw$/i.test(col);
}

function isJsonBlob(v: unknown): boolean {
  // Arrays are left alone — String(["Home Loan","Gold Loan"]) still reads fine.
  if (v !== null && typeof v === "object" && !Array.isArray(v)) return true;
  if (typeof v !== "string") return false;
  const t = v.trim();
  return t.startsWith("{") && t.endsWith("}");
}

/** Drops display-hostile columns from every row, preserving column order. */
function stripInternalCols(
  rows: Record<string, unknown>[],
): Record<string, unknown>[] {
  const drop = new Set(
    Object.keys(rows[0]).filter(
      (k) => isInternalCol(k) || rows.some((r) => isJsonBlob(r[k])),
    ),
  );
  if (drop.size === 0) return rows;
  return rows.map((r) =>
    Object.fromEntries(Object.entries(r).filter(([k]) => !drop.has(k))),
  );
}

// ---------------------------------------------------------------------------
// MetricCard — single aggregate row (1 row or a few scalar results)
// ---------------------------------------------------------------------------

function MetricCard({ rows }: { rows: Record<string, unknown>[] }) {
  // If multiple rows, treat as repeated metrics (e.g. sum + count)
  const row = rows[0];
  const entries = Object.entries(row).filter(
    ([, v]) => v !== null && v !== undefined,
  );

  if (entries.length === 1) {
    // Single metric — hero number
    const [col, val] = entries[0];
    return (
      <Card className="border-accent/30 bg-accent/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="h-4 w-4 text-accent" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {colLabel(col)}
            </span>
          </div>
          <p className="text-3xl font-bold text-accent leading-none">
            {fmtValue(col, val)}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Multi-metric grid
  return (
    <Card className="border-accent/30 bg-accent/5">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="h-4 w-4 text-accent" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Result
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {entries.map(([col, val]) => (
            <div key={col}>
              <p className="text-[10px] text-muted-foreground mb-0.5">{colLabel(col)}</p>
              <p
                className={cn(
                  "text-xl font-bold leading-none",
                  isMoneyCol(col) ? "text-gold" : isRateCol(col) ? "text-accent" : "text-foreground",
                )}
              >
                {fmtValue(col, val)}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// LeaderboardCard — ranked list with inline bars
// ---------------------------------------------------------------------------

const RANK_STYLES = [
  "bg-gold text-gold-foreground",
  "bg-muted text-foreground",
  "bg-warning/20 text-warning-foreground",
];

function RankBadge({ rank }: { rank: number }) {
  const style = rank <= 3 ? RANK_STYLES[rank - 1] : "bg-muted/60 text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold shrink-0",
        style,
      )}
    >
      {rank}
    </span>
  );
}

function LeaderboardCard({ rows }: { rows: Record<string, unknown>[] }) {
  const [expanded, setExpanded] = useState(false);
  const keys = Object.keys(rows[0]);

  const nameCol =
    findCol(keys, "name", "banker", "partner", "applicant", "city", "status", "lender") ??
    keys[0];
  const valueCol =
    findCol(keys, "disburs", "amount", "payout", "total", "count", "leads", "rate", "pct") ??
    keys.find((k) => k !== nameCol && typeof rows[0][k] === "number") ??
    keys[1];
  const rankCol = findCol(keys, "rank");

  const maxVal = rows.reduce((m, r) => {
    const v = typeof r[valueCol] === "number" ? (r[valueCol] as number) : 0;
    return Math.max(m, v);
  }, 0);

  const PREVIEW = 3;
  const visible = expanded ? rows : rows.slice(0, PREVIEW);
  const remainder = rows.length - PREVIEW;

  return (
    <Card className="border-gold/30 bg-gold/5">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="h-4 w-4 text-gold" />
          <span className="text-xs font-semibold text-gold uppercase tracking-wide">
            Rankings
          </span>
          <span className="ml-auto text-xs text-muted-foreground">
            {rows.length} results
          </span>
        </div>

        <div className="space-y-2.5">
          {visible.map((row, idx) => {
            const rank = rankCol
              ? (typeof row[rankCol] === "number" ? (row[rankCol] as number) : idx + 1)
              : idx + 1;
            const name = String(row[nameCol] ?? "—");
            const val = typeof row[valueCol] === "number" ? (row[valueCol] as number) : null;

            return (
              <div key={idx} className="flex items-center gap-2.5">
                <RankBadge rank={rank} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{name}</p>
                  {val !== null && maxVal > 0 && (
                    <InlineBar
                      value={val}
                      max={maxVal}
                      variant={rank === 1 ? "gold" : "accent"}
                      className="mt-1"
                    />
                  )}
                </div>
                {val !== null && (
                  <span className="text-sm font-semibold shrink-0 tabular-nums">
                    {fmtValue(valueCol, val)}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {remainder > 0 && (
          <button
            onClick={() => setExpanded((p) => !p)}
            className="mt-3 w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            {expanded ? (
              <><ChevronUp className="h-3 w-3" /> Show less</>
            ) : (
              <><ChevronDown className="h-3 w-3" /> +{remainder} more</>
            )}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TrendCard — sparkline for time-series data
// ---------------------------------------------------------------------------

function TrendCard({ rows }: { rows: Record<string, unknown>[] }) {
  const keys = Object.keys(rows[0]);
  const dateCol = keys.find((k) => isDateCol(k)) ?? keys[0];
  const valueCol =
    keys.find((k) => k !== dateCol && isMoneyCol(k)) ??
    keys.find((k) => k !== dateCol && isNumericCol(k, rows[0][k])) ??
    keys[1];

  const values = rows
    .map((r) => (typeof r[valueCol] === "number" ? (r[valueCol] as number) : null))
    .filter((v): v is number => v !== null);

  if (values.length < 2) return <LeaderboardCard rows={rows} />;

  const first = values[0];
  const last = values[values.length - 1];
  const pctChange = first > 0 ? ((last - first) / first) * 100 : 0;
  const isPositive = pctChange >= 0;
  const peak = Math.max(...values);
  const peakIdx = values.indexOf(peak);
  const peakLabel = String(rows[peakIdx][dateCol] ?? "");

  return (
    <Card className="border-accent/30 bg-accent/5">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-accent" />
          <span className="text-xs font-semibold text-accent uppercase tracking-wide">
            {colLabel(valueCol)} Trend
          </span>
          <span
            className={cn(
              "ml-auto text-xs font-semibold",
              isPositive ? "text-success" : "text-destructive",
            )}
          >
            {isPositive ? "+" : ""}
            {pctChange.toFixed(1)}%
          </span>
        </div>

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <div className="text-lg font-bold">{fmtValue(valueCol, last)}</div>
            <div className="text-xs text-muted-foreground">Latest</div>
          </div>
          <Sparkline
            values={values}
            width={96}
            height={32}
            className={cn("text-accent", !isPositive && "text-destructive")}
          />
        </div>

        {peakLabel && (
          <p className="text-[10px] text-muted-foreground mt-2">
            Peak: {fmtValue(dateCol, rows[peakIdx][dateCol])} — {fmtValue(valueCol, peak)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TeamReportCard — per-manager login + disbursement report
// ---------------------------------------------------------------------------

function TeamReportCard({ rows }: { rows: Record<string, unknown>[] }) {
  const keys = Object.keys(rows[0]);

  const nameKey    = keys.find((k) => /manager_name/i.test(k)) ?? keys.find((k) => /name/i.test(k)) ?? keys[0];
  const loginKey   = keys.find((k) => /login/i.test(k) && !/target/i.test(k));
  const filesKey   = keys.find((k) => /total_files|total_leads/i.test(k));
  const disbKey    = keys.find((k) => /disburs/i.test(k) && !/target/i.test(k));
  const targetKey  = keys.find((k) => /monthly_target|target/i.test(k) && !/count/i.test(k));

  let totalLogins = 0, totalFiles = 0, totalDisbursed = 0, totalTarget = 0;

  const managerRows = rows.map((row) => {
    const logins    = loginKey  ? Number(row[loginKey]  ?? 0) : 0;
    const files     = filesKey  ? Number(row[filesKey]  ?? 0) : 0;
    const disbursed = disbKey   ? Number(row[disbKey]   ?? 0) : 0;
    const target    = targetKey ? Number(row[targetKey] ?? 0) : 0;
    totalLogins    += logins;
    totalFiles     += files;
    totalDisbursed += disbursed;
    totalTarget    += target;
    return { name: String(row[nameKey] ?? "Unknown"), logins, files, disbursed, target };
  });

  return (
    <Card className="border-accent/30 bg-accent/5">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-accent" />
          <span className="text-xs font-semibold text-accent uppercase tracking-wide">
            Team Report
          </span>
          <span className="ml-auto text-xs text-muted-foreground">
            {rows.length} sales manager{rows.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="space-y-3">
          {managerRows.map((m, i) => (
            <div key={i} className="border-b border-border/40 pb-3 last:border-0 last:pb-0">
              <p className="text-sm font-semibold text-foreground mb-1">{m.name}</p>
              <div className="space-y-0.5 pl-1">
                {filesKey && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{m.logins}</span>
                    {" / "}
                    <span className="font-medium text-foreground">{m.files}</span>
                    {" files"}
                  </p>
                )}
                {(disbKey || targetKey) && (
                  <p className="text-xs text-muted-foreground">
                    <span className={cn("font-medium", m.disbursed > 0 ? "text-success" : "text-foreground")}>
                      {m.disbursed > 0 ? fmtMoney(m.disbursed) : "0"}
                    </span>
                    {" / "}
                    <span className="font-medium text-foreground">
                      {m.target > 0 ? fmtMoney(m.target) : "0"}
                    </span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {(filesKey || disbKey || targetKey) && (
          <div className="mt-3 pt-3 border-t border-border/60 space-y-1">
            {(disbKey || targetKey) && (
              <p className="text-xs text-muted-foreground">
                Done so far:{" "}
                <span className={cn("font-semibold", totalDisbursed > 0 ? "text-success" : "text-foreground")}>
                  {totalDisbursed > 0 ? fmtMoney(totalDisbursed) : "0"}
                </span>
                {" / "}
                <span className="font-semibold text-foreground">
                  {totalTarget > 0 ? fmtMoney(totalTarget) : "0"}
                </span>
              </p>
            )}
            {filesKey && (
              <p className="text-xs text-muted-foreground">
                Files done:{" "}
                <span className="font-semibold text-foreground">{totalLogins}</span>
                {" / "}
                <span className="font-semibold text-foreground">{totalFiles}</span>
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// RecordCard — actionable record list (leads, bankers, channel partners)
// ---------------------------------------------------------------------------

/**
 * Jumps from a chat result straight to the page that owns the record.
 * Rendered only when `buildRecordLink` resolved a route, so a row without an
 * id (or a payout grid with nothing to seed) simply shows no button.
 */
function OpenButton({ to, label = "Open" }: { to: string; label?: string }) {
  const navigate = useNavigate();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 text-xs gap-1.5 shrink-0"
      onClick={() => navigate(to)}
    >
      <ExternalLink className="h-3 w-3" />
      {label}
    </Button>
  );
}

function LeadRecord({ row }: { row: Record<string, unknown> }) {
  const name =
    `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() ||
    String(row.applicant_name ?? row.name ?? "Unknown");
  const phone = String(row.mobile ?? row.phone ?? "");
  const status = String(row.status ?? "");
  const loanType = String(row.loanType ?? row.loan_type ?? "");
  const amount = row.loanAmount ?? row.loan_amount ?? row.disbursedAmount;
  const city = String(row.customerCity ?? row.city ?? "");
  const id = String(row.id ?? "");

  const statusColors: Record<string, string> = {
    disbursed:         "bg-success/10 text-success",
    sanctioned:        "bg-accent/10 text-accent",
    rejected:          "bg-destructive/10 text-destructive",
    new_lead:          "bg-primary/10 text-primary",
    followup_later:    "bg-warning/10 text-warning",
    disbursal_pending: "bg-gold/10 text-gold",
  };
  const statusColor = statusColors[status] ?? "bg-muted text-muted-foreground";

  return (
    <div className="flex flex-col gap-2 py-2.5 border-b border-border/50 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">{name}</p>
          {phone && (
            <p className="text-xs text-muted-foreground mt-0.5 font-medium">{phone}</p>
          )}
          {(loanType || amount || city) && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {loanType.replace(/_/g, " ")}
              {amount ? ` · ${fmtMoney(Number(amount))}` : ""}
              {city ? ` · ${city}` : ""}
            </p>
          )}
        </div>
        {status && (
          <Badge
            variant="outline"
            className={cn("text-[10px] shrink-0 border-0 px-1.5 py-0.5", statusColor)}
          >
            {status.replace(/_/g, " ")}
          </Badge>
        )}
      </div>
      {(phone || id) && (
        <div className="flex gap-2">
          {phone && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0 shrink-0"
                asChild
              >
                <a href={`tel:${phone}`} title={phone}>
                  <Phone className="h-3.5 w-3.5" />
                </a>
              </Button>
              <a
                href={whatsappHref(phone)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-7 flex-1 items-center justify-center gap-1.5 rounded-md bg-[#25D366] hover:bg-[#1ebe5d] text-white transition-colors text-xs font-medium px-2"
              >
                <MessageCircle className="h-3.5 w-3.5 shrink-0" />
                WhatsApp
              </a>
            </>
          )}
          {id && <OpenButton to={`/leads/${id}`} />}
        </div>
      )}
    </div>
  );
}

function BankerRecord({ row }: { row: Record<string, unknown> }) {
  const name  = String(row.name ?? "Unknown");
  const bank  = String(row.bank ?? row.bankName ?? "");
  const role  = String(row.designation ?? "");
  const city  = String(row.region ?? row.city ?? "");
  const phone = String(row.phone ?? row.mobile ?? "");
  const link  = buildRecordLink("bankers", row);

  return (
    <div className="flex flex-col gap-2 py-2.5 border-b border-border/50 last:border-0">
      <div>
        <p className="text-sm font-semibold leading-tight">{name}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {bank}
          {role ? ` · ${role}` : ""}
          {city ? ` · ${city}` : ""}
        </p>
      </div>
      {(phone || link) && (
        <div className="flex gap-2">
          {phone && (
            <>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" asChild>
                <a href={`tel:${phone}`}>
                  <Phone className="h-3 w-3" />
                  {phone}
                </a>
              </Button>
              <a
                href={whatsappHref(phone)}
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#25D366] hover:bg-[#1ebe5d] text-white shrink-0 transition-colors"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            </>
          )}
          {link && <OpenButton to={link} />}
        </div>
      )}
    </div>
  );
}

// `entity` decides which detail route this partner belongs to — channel
// partners and DSA partners share a row shape but live on different pages.
function PartnerRecord({ row, entity }: { row: Record<string, unknown>; entity?: string }) {
  const name  = String(row.name ?? "Unknown");
  const city  = String(row.city ?? "");
  const type  = String(row.type ?? "");
  const phone = String(row.whatsappNumber ?? row.phone ?? row.mobile ?? "");
  const active = row.isActive;
  const link  = buildRecordLink(entity, row);

  return (
    <div className="flex flex-col gap-2 py-2.5 border-b border-border/50 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">{name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {type.replace(/_/g, " ")}
            {city ? ` · ${city}` : ""}
          </p>
        </div>
        {active !== undefined && (
          <Badge
            variant="outline"
            className={cn(
              "text-[10px] shrink-0 border-0 px-1.5 py-0.5",
              active ? "bg-success/10 text-success" : "bg-muted text-muted-foreground",
            )}
          >
            {active ? "Active" : "Inactive"}
          </Badge>
        )}
      </div>
      {(phone || link) && (
        <div className="flex gap-2">
          {phone && (
            <>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" asChild>
                <a href={`tel:${phone}`}>
                  <Phone className="h-3 w-3" />
                  {phone}
                </a>
              </Button>
              <a
                href={whatsappHref(phone)}
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#25D366] hover:bg-[#1ebe5d] text-white shrink-0 transition-colors"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            </>
          )}
          {link && <OpenButton to={link} />}
        </div>
      )}
    </div>
  );
}

function GenericRecord({ row, entity }: { row: Record<string, unknown>; entity?: string }) {
  // Fallback for SQL result records with no entity context
  const keys   = Object.keys(row);
  const nameCol = findCol(keys, "name", "applicant", "banker", "partner", "city");
  const phoneCol = findCol(keys, "phone", "mobile", "whatsapp");
  const name = nameCol ? String(row[nameCol] ?? "—") : null;
  // Payout grids land here; they carry no id-addressed route but can still
  // seed the Payouts page cascade.
  const link = buildRecordLink(entity, row);

  return (
    <div className="py-2 border-b border-border/50 last:border-0">
      {name && <p className="text-sm font-semibold mb-1">{name}</p>}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {keys
          .filter((k) => k !== nameCol)
          .slice(0, 5)
          .map((k) => (
            <span key={k} className="text-xs text-muted-foreground">
              <span className="text-foreground/70">{colLabel(k)}:</span>{" "}
              {fmtValue(k, row[k])}
            </span>
          ))}
      </div>
      {((phoneCol && row[phoneCol]) || link) && (
        <div className="flex gap-2 mt-2">
          {phoneCol && row[phoneCol] && (
            <>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" asChild>
                <a href={`tel:${row[phoneCol]}`}>
                  <Phone className="h-3 w-3" />
                  {String(row[phoneCol])}
                </a>
              </Button>
              <a
                href={whatsappHref(String(row[phoneCol]))}
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#25D366] hover:bg-[#1ebe5d] text-white shrink-0 transition-colors"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            </>
          )}
          {link && (
            <OpenButton
              to={link}
              label={entity === "payoutRates" ? "Open payout" : "Open"}
            />
          )}
        </div>
      )}
    </div>
  );
}

function RecordCard({
  rows,
  entity,
}: {
  rows: Record<string, unknown>[];
  entity?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 4;
  const visible = expanded ? rows : rows.slice(0, PREVIEW);
  const remainder = rows.length - PREVIEW;

  const isLeads   = entity === "leads";
  const isBankers = entity === "bankers";
  const isPartners = entity === "channelPartners" || entity === "dsaPartners" || entity === "vendors";

  function renderRow(row: Record<string, unknown>, i: number) {
    if (isLeads)    return <LeadRecord    key={i} row={row} />;
    if (isBankers)  return <BankerRecord  key={i} row={row} />;
    if (isPartners) return <PartnerRecord key={i} row={row} entity={entity} />;
    return <GenericRecord key={i} row={row} entity={entity} />;
  }

  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {rows.length} Record{rows.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div>{visible.map((row, i) => renderRow(row, i))}</div>

        {remainder > 0 && (
          <button
            onClick={() => setExpanded((p) => !p)}
            className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            {expanded ? (
              <><ChevronUp className="h-3 w-3" /> Show less</>
            ) : (
              <><ChevronDown className="h-3 w-3" /> +{remainder} more</>
            )}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Public dispatcher
// ---------------------------------------------------------------------------

interface AnswerCardProps {
  rows: Record<string, unknown>[];
  resultType: ResultType;
  entity?: string;
}

export function AnswerCard({ rows: sourceRows, resultType, entity }: AnswerCardProps) {
  if (sourceRows.length === 0) return null;

  // Every card below reads columns off the row generically, so the internal
  // ones are removed once here rather than in each card.
  const rows = stripInternalCols(sourceRows);
  // Nothing displayable survived — the prose answer stands on its own.
  if (Object.keys(rows[0]).length === 0) return null;

  // Detect trend: ranked_list or aggregate with a date column and ≥3 rows
  if (rows.length >= 3 && resultType !== "team_report") {
    const keys = Object.keys(rows[0]);
    const hasTrendCol = keys.some((k) => isDateCol(k));
    const hasValueCol = keys.some((k) => isMoneyCol(k) || (k !== keys[0] && isNumericCol(k, rows[0][k])));
    if (hasTrendCol && hasValueCol) {
      return <TrendCard rows={rows} />;
    }
  }

  switch (resultType) {
    case "aggregate":    return <MetricCard rows={rows} />;
    case "ranked_list":  return <LeaderboardCard rows={rows} />;
    case "list":         return <RecordCard rows={rows} entity={entity} />;
    case "team_report":  return <TeamReportCard rows={rows} />;
    default:             return null;
  }
}
